import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Users, Music, Beer, CheckCircle, Clock, Radio, Wifi, UserPlus, Calendar, ArrowRight, Image as ImageIcon, Send } from 'lucide-react';
import { API_BASE_URL } from '@/lib/api';
import { formatRelativeTime } from '@/lib/utils';

const API = API_BASE_URL;
const LOGO_URL = "/logo.png";
export default function LivePage() {
  const [searchParams] = useSearchParams();
  const isKiosk = searchParams.get('kiosk') === 'true';
  const [data, setData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/api/live`).then(r => {
      setData(r.data);
      setLastUpdate(new Date());
    }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, isKiosk ? 4000 : 5000);
    return () => clearInterval(iv);
  }, [load, isKiosk]);

  if (!data) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isKiosk) return <KioskView data={data} lastUpdate={lastUpdate} />;
  return <StandardView data={data} lastUpdate={lastUpdate} />;
}

// ═══════════════════════════════════════════════════════
// KIOSK MODE — Full-screen, TV/projector optimized
// ═══════════════════════════════════════════════════════
function KioskView({ data, lastUpdate }) {
  const { checkin, music, feed } = data;
  const pct = checkin.total_guests > 0 ? Math.round((checkin.checked_in / checkin.total_guests) * 100) : 0;
  const nowPlaying = music.now_playing?.track;

  return (
    <div className="h-screen w-screen bg-[#020617] text-slate-100 flex flex-col overflow-hidden select-none" data-testid="kiosk-view">
      {/* Top bar — minimal */}
      <div className="flex items-center justify-between px-8 py-4 flex-shrink-0">
        <div className="flex items-center gap-4">
          <img src={LOGO_URL} alt="Molin30" className="h-8" />
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            <span className="text-sm font-semibold text-emerald-400 tracking-wide">LIVE</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-500">{lastUpdate ? lastUpdate.toLocaleTimeString() : ''}</span>
          <Wifi className="w-4 h-4 text-emerald-400" />
        </div>
      </div>

      {/* Hero — Giant check-in counter */}
      <div className="flex-1 flex flex-col items-center justify-center px-8" data-testid="kiosk-hero">
        <p className="text-sm font-bold uppercase tracking-[0.3em] text-slate-500 mb-4">
          Gæster tjekket ind
        </p>
        <div className="flex items-baseline gap-4 mb-6">
          <span className="text-[12rem] leading-none font-bold tabular-nums tracking-tight bg-gradient-to-b from-blue-400 via-blue-300 to-slate-400 bg-clip-text text-transparent transition-all duration-1000"
            style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="kiosk-checkin-count">
            {checkin.checked_in}
          </span>
          <span className="text-5xl text-slate-600 font-light" style={{ fontFamily: "'Outfit', sans-serif" }}>/</span>
          <span className="text-5xl text-slate-500 font-light tabular-nums" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {checkin.total_guests}
          </span>
        </div>

        {/* Progress bar */}
        <div className="w-full max-w-2xl mb-3">
          <div className="w-full h-4 bg-slate-800/60 rounded-full overflow-hidden border border-slate-700/30">
            <div className="h-full rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-emerald-500 transition-all duration-1000 ease-out"
              style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="flex items-center gap-8 text-sm">
          <span className="text-emerald-400 font-semibold">{pct}% ankommet</span>
          <span className="text-slate-500">{checkin.attending} deltager</span>
          <span className="text-amber-400/80">{checkin.not_arrived} mangler</span>
        </div>
      </div>

      {/* Bottom bar — Music + Activity ticker */}
      <div className="flex-shrink-0 border-t border-slate-800/60 bg-slate-900/40">
        <div className="flex items-stretch divide-x divide-slate-800/50" style={{ height: '80px' }}>
          {/* Music status */}
          <div className="flex items-center gap-6 px-8 flex-shrink-0 min-w-[520px]" data-testid="kiosk-music">
            <Radio className="w-5 h-5 text-purple-400" />
            {nowPlaying ? (
              <div className="flex items-center gap-3 min-w-0">
                {nowPlaying.album_art && <img src={nowPlaying.album_art} alt="" className="w-12 h-12 rounded object-cover" />}
                <div className="min-w-0">
                  <p className="text-xs text-purple-300">Nu spiller</p>
                  <p className="text-sm text-slate-200 truncate">{nowPlaying.name}</p>
                  <p className="text-xs text-slate-500 truncate">{nowPlaying.artist}</p>
                </div>
              </div>
            ) : (
              <span className="text-sm text-slate-500">Der afspilles ingen musik.</span>
            )}
          </div>

          {/* Activity ticker */}
          <div className="flex-1 flex items-center px-8 overflow-hidden" data-testid="kiosk-ticker">
            <div className="flex items-center gap-6 animate-ticker">
              {feed.length === 0 ? (
                <span className="text-sm text-slate-600">Venter på aktivitet...</span>
              ) : feed.slice(0, 8).map((item, i) => (
                <span key={i} className="text-sm text-slate-400 whitespace-nowrap flex items-center gap-2 flex-shrink-0">
                  {item.type === 'checkin' && <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />}
                  {item.type === 'music' && <Music className="w-3.5 h-3.5 text-purple-400" />}
                  {item.type === 'register' && <UserPlus className="w-3.5 h-3.5 text-blue-400" />}
                  {item.type === 'rsvp' && <Calendar className="w-3.5 h-3.5 text-indigo-400" />}
                  {item.type === 'invite' && <Send className="w-3.5 h-3.5 text-cyan-400" />}
                  {item.type === 'image' && <ImageIcon className="w-3.5 h-3.5 text-pink-400" />}
                  {item.type === 'bar' && <Beer className="w-3.5 h-3.5 text-amber-400" />}
                  {item.type === 'bar_ready' && <Beer className="w-3.5 h-3.5 text-emerald-400" />}
                  {item.type === 'bar_done' && <Beer className="w-3.5 h-3.5 text-slate-400" />}
                  {item.text}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Kiosk ticker animation */}
      <style>{`
        @keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-ticker { animation: ticker 30s linear infinite; display: flex; gap: 3rem; }
        .animate-ticker:hover { animation-play-state: paused; }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// STANDARD VIEW — Original /live page
// ═══════════════════════════════════════════════════════
function StandardView({ data, lastUpdate }) {
  const { checkin, music, bar = {}, images = {}, rsvp = {}, invitations = {}, system, feed } = data;
  const checkinPct = checkin.total_guests > 0 ? Math.round((checkin.checked_in / checkin.total_guests) * 100) : 0;
  const nowPlaying = music.now_playing?.track;

  const feedIcons = {
    checkin: <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />,
    music: <Music className="w-3.5 h-3.5 text-purple-400" />,
    register: <UserPlus className="w-3.5 h-3.5 text-blue-400" />,
    rsvp: <Calendar className="w-3.5 h-3.5 text-indigo-400" />,
    invite: <Send className="w-3.5 h-3.5 text-cyan-400" />,
    image: <ImageIcon className="w-3.5 h-3.5 text-pink-400" />,
    bar: <Beer className="w-3.5 h-3.5 text-amber-400" />,
    bar_ready: <Beer className="w-3.5 h-3.5 text-emerald-400" />,
    bar_done: <Beer className="w-3.5 h-3.5 text-slate-400" />,
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100" data-testid="live-page">
      <header className="sticky top-0 z-30 bg-slate-900/80 backdrop-blur-xl border-b border-slate-800" data-testid="live-header">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Molin30" className="h-7" />
            <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span className="text-xs font-medium text-emerald-400">LIVE</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 hidden sm:block">{lastUpdate ? `Opdateret ${lastUpdate.toLocaleTimeString()}` : ''}</span>
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <Wifi className="w-3.5 h-3.5 text-emerald-400" />
              <span className="hidden sm:inline">{system.phase}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-testid="live-checkin-stats">
          <StatCard label="Gæster i alt" value={checkin.total_guests} icon={Users} color="blue" testId="live-total-guests" />
          <StatCard label="Deltager" value={checkin.attending} icon={Calendar} color="indigo" testId="live-attending" />
          <StatCard label="Tjekket ind" value={checkin.checked_in} icon={CheckCircle} color="emerald" testId="live-checked-in" />
          <StatCard label="Ikke ankommet" value={checkin.not_arrived} icon={Clock} color="amber" testId="live-not-arrived" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4 mb-6" data-testid="live-activity-summary">
          <MiniStat label="Aktive bar" value={(bar.pending || 0) + (bar.preparing || 0) + (bar.ready || 0)} icon={Beer} color="amber" />
          <MiniStat label="Musik" value={(music.pending || 0) + (music.approved || 0) + (music.queued || 0)} icon={Music} color="purple" />
          <MiniStat label="Billeder" value={images.uploads || 0} icon={ImageIcon} color="pink" />
          <MiniStat label="RSVPs" value={rsvp.submitted || 0} icon={Calendar} color="indigo" />
          <MiniStat label="Invitationer" value={invitations.sent || 0} icon={Send} color="cyan" />
        </div>

        <div className="bg-slate-800/40 border border-purple-500/20 rounded-xl p-4 sm:p-5 mb-6" data-testid="live-now-playing">
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-purple-400 mb-4 flex items-center gap-2">
            <Music className="w-3.5 h-3.5" /> Nu spiller
          </h3>
          {nowPlaying ? (
            <div className="flex items-center gap-4 min-w-0">
              {nowPlaying.album_art ? (
                <img src={nowPlaying.album_art} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
              ) : (
                <div className="w-20 h-20 rounded-lg bg-slate-900/60 flex items-center justify-center flex-shrink-0">
                  <Music className="w-7 h-7 text-slate-500" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-lg font-semibold text-slate-100 truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>{nowPlaying.name}</p>
                <p className="text-sm text-slate-400 truncate">{nowPlaying.artist}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500">Der afspilles ingen musik.</p>
          )}
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 sm:p-5 mb-6" data-testid="live-checkin-progress">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Check-in status</span>
            <span className="text-lg sm:text-2xl font-semibold text-slate-100" style={{ fontFamily: "'Outfit', sans-serif" }}>{checkinPct}%</span>
          </div>
          <div className="w-full h-3 bg-slate-900/60 rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-1000 ease-out" style={{ width: `${checkinPct}%` }} />
          </div>
          <div className="flex justify-between mt-2 text-xs text-slate-500">
            <span>{checkin.checked_in} ankommet</span>
            <span>{checkin.not_arrived} mangler</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="lg:col-span-1 bg-slate-800/40 border border-slate-700/50 rounded-xl p-5" data-testid="live-music-status">
            <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-purple-400 mb-4 flex items-center gap-2">
              <Radio className="w-3.5 h-3.5" /> Musik
            </h3>
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="text-center p-3 bg-slate-900/40 rounded-lg">
                <p className="text-2xl font-semibold text-amber-400" style={{ fontFamily: "'Outfit', sans-serif" }}>{music.pending}</p>
                <p className="text-xs text-slate-500 mt-0.5">Afventer</p>
              </div>
              <div className="text-center p-3 bg-slate-900/40 rounded-lg">
                <p className="text-2xl font-semibold text-emerald-400" style={{ fontFamily: "'Outfit', sans-serif" }}>{music.approved}</p>
                <p className="text-xs text-slate-500 mt-0.5">Godkendt</p>
              </div>
              <div className="text-center p-3 bg-slate-900/40 rounded-lg">
                <p className="text-2xl font-semibold text-blue-400" style={{ fontFamily: "'Outfit', sans-serif" }}>{music.queued}</p>
                <p className="text-xs text-slate-500 mt-0.5">I kø</p>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-5">
              <TinyMetric label="Bar" value={(bar.pending || 0) + (bar.preparing || 0)} color="text-amber-400" />
              <TinyMetric label="Klar" value={bar.ready || 0} color="text-emerald-400" />
              <TinyMetric label="Billeder" value={images.uploads || 0} color="text-pink-400" />
              <TinyMetric label="RSVP" value={rsvp.submitted || 0} color="text-indigo-400" />
            </div>
            {music.latest ? (
              <div className="border-t border-slate-700/30 pt-4">
                <p className="text-xs text-slate-500 mb-1">Senest godkendt</p>
                <p className="text-sm font-medium text-slate-200 truncate">{music.latest.song_title}</p>
                {music.latest.artist && <p className="text-xs text-slate-400 truncate">{music.latest.artist}</p>}
                <p className="text-xs text-slate-500 mt-1">fra {music.latest.guest_name}</p>
              </div>
            ) : (
              <p className="text-xs text-slate-500 border-t border-slate-700/30 pt-4">Ingen godkendte sange endnu</p>
            )}
          </div>

          <div className="lg:col-span-2 bg-slate-800/40 border border-slate-700/50 rounded-xl p-5" data-testid="live-activity-feed">
            <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-blue-400 mb-4">Aktivitetsfeed</h3>
            {feed.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">Venter på aktivitet...</p>
            ) : (
              <div className="space-y-0">
                {feed.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 py-2.5 border-b border-slate-700/20 last:border-0 animate-fade-in"
                    style={{ animationDelay: `${i * 0.04}s`, opacity: 0 }}>
                    <div className="mt-0.5 flex-shrink-0">{feedIcons[item.type] || <ArrowRight className="w-3.5 h-3.5 text-slate-500" />}</div>
                    <p className="text-sm text-slate-300 flex-1 min-w-0">{item.text}</p>
                    <span className="text-xs text-slate-600 flex-shrink-0 tabular-nums text-right">
                      <span className="block">{formatRelativeTime(item.time)}</span>
                      <span className="block">{item.time ? new Date(item.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-3 text-xs text-slate-600" data-testid="live-system-status">
          <div className="flex items-center gap-1.5">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
            </span>
            Systemet er online
          </div>
          <span>&middot;</span>
          <span>SMTP {system.smtp ? 'Til' : 'Fra'}</span>
          <span>&middot;</span>
          <span>Lager {system.storage ? 'Til' : 'Fra'}</span>
          <span>&middot;</span>
          <span>Autoopdatering 10s</span>
          <span>&middot;</span>
          <span>Molin30 gæsteportal</span>
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, testId }) {
  const colors = { blue: 'border-blue-500/20 text-blue-400', indigo: 'border-indigo-500/20 text-indigo-400', emerald: 'border-emerald-500/20 text-emerald-400', amber: 'border-amber-500/20 text-amber-400' };
  const c = colors[color] || colors.blue;
  return (
    <div className={`bg-slate-800/40 border ${c.split(' ')[0]} rounded-xl p-4 sm:p-5`} data-testid={testId}>
      <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${c.split(' ')[1]} mb-2`} />
      <p className="text-3xl sm:text-4xl font-semibold text-slate-100 tabular-nums" style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</p>
      <p className="text-xs text-slate-500 mt-1">{label}</p>
    </div>
  );
}

function MiniStat({ label, value, icon: Icon, color }) {
  const colors = {
    amber: 'border-amber-500/20 text-amber-400',
    purple: 'border-purple-500/20 text-purple-400',
    pink: 'border-pink-500/20 text-pink-400',
    indigo: 'border-indigo-500/20 text-indigo-400',
    cyan: 'border-cyan-500/20 text-cyan-400',
  };
  const c = colors[color] || colors.cyan;
  return (
    <div className={`bg-slate-800/30 border ${c.split(' ')[0]} rounded-xl p-3 flex items-center gap-3`}>
      <Icon className={`w-4 h-4 ${c.split(' ')[1]} flex-shrink-0`} />
      <div>
        <p className="text-lg font-semibold text-slate-100 tabular-nums leading-tight">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  );
}

function TinyMetric({ label, value, color }) {
  return (
    <div className="text-center p-2 bg-slate-900/40 rounded-lg">
      <p className={`text-lg font-semibold ${color}`}>{value}</p>
      <p className="text-[11px] text-slate-500">{label}</p>
    </div>
  );
}
